import logger from '../../log';
import tracksMatch from '../../reporter/util/tracksMatch';
import { CleanupMethod } from '../BaseInteraction';
import {
  ConstructedEvents,
  TechEngagementMethods
} from '../../common/Reporter/constants';
import { Handler } from '@kollective-frontend/ksdk-api';
import { ShakaPlayer } from '../interfaces';
import { TrackInfo, TechApiState } from '../../reporter/constants';
import { dispatchPlayerEvent } from '../../events/dispatch';
import { noop } from '../../common/utils/callOrNoop';
import {
  setupTechApiFunction,
  tearDownTechApiFunction,
  TechApi
} from '../../bootloader/tech';

const log = logger.getLogger(__filename);

// AdaptationEvents are triggered when the active track changes, but are not
// fired when the application calls certain select* methods. For more
// information see:
// https://shaka-player-demo.appspot.com/docs/api/shaka.Player.html#.event:AbrStatusChangedEvent
export const SHAKA_ADAPTATION_EVENT = 'adaptation';
export const SHAKA_AUDIO_CHANGED = 'audiotrackchanged';
export const SHAKA_RATE_CHANGED = 'ratechange';
export const SHAKA_TEXT_CHANGED = 'textchanged';
export const SHAKA_TEXT_TRACK_VIS = 'texttrackvisibility';
export const SHAKA_VARIANT_CHANGED = 'variantchanged';

const {
  playbackbitratechanged: { code: pbc },
  audiochanged: { code: audioc },
  captionchanged: { code: capc }
} = ConstructedEvents;
const { AUDIOCHANGED, BITRATECHANGED, CAPTIONCHANGED } = TechEngagementMethods;

//#region Interfaces
export interface ShakaVariantTrack {
  active: boolean;
  label: string;
  language: string;
  bandwidth: number;
}
type MaybeShakaTrack = ShakaVariantTrack | undefined;

export interface ShakaPlayerExtended extends ShakaPlayer {
  removeEventListener: (type: string, listener: unknown) => void;
  getAudioTracks?: () => ShakaVariantTrack[];
  getTextTracks?: () => ShakaVariantTrack[];
  getVariantTracks?: () => ShakaVariantTrack[];
}
//#endregion Interfaces
//#region Utility functions
/**
 * Get the active track from lists of tracks.
 *
 * NOTE: exported for testing only
 *
 * @param name - kind of tracks
 * @param p1 - primary tracks
 * @param p2 - secondary tracks
 * @returns If there are no tracks found will return undefined, if there are
 * tracks but none active, the function will return an empty track info. If
 * there are multiple active tracks found will return an active track. (most
 * players at this point don't allow multiple active tracks, and we'll have to
 * change things if this changes)
 */
export function getShakaTrack(
  name: string,
  p1?: ShakaVariantTrack[],
  p2?: ShakaVariantTrack[]
): TrackInfo | undefined {
  const tracks = p1 ?? p2;
  if (!tracks || tracks.length < 1) {
    log.trace('Found no', name, 'tracks');
    return;
  }
  const track = tracks.reduce(
    (acc: MaybeShakaTrack, t) => (t.active ? t : acc),
    undefined
  );
  if (!track) {
    log.trace('Found no enabled', name, 'track:', { p1, p2 });
    return { lang: '', name: '' };
  }
  log.trace(name, 'track:', track);
  return { lang: track.language, name: track.label };
}

/**
 * Get the active audio track information.
 * {@link getShakaTrack}
 *
 * NOTE: Exported for unit tests
 *
 * @param player - the shaka player
 * @returns track info if available
 */
export function getAudioTrackInfo(
  player: ShakaPlayerExtended
): TrackInfo | undefined {
  const audioTracks = player.getAudioTracks?.();
  const variants = player.getVariantTracks?.();
  return getShakaTrack('audio', audioTracks, variants);
}

/**
 * Get the active audio track information.
 * {@link getShakaTrack}
 *
 * NOTE: Exported for unit tests
 *
 * @param player - the shaka player
 * @returns track info if available
 */
export function getTextTrackInfo(
  player: ShakaPlayerExtended
): TrackInfo | undefined {
  return getShakaTrack('caption', player.getTextTracks?.());
}

/**
 * Get the current bitrate.
 *
 * NOTE: Exported for unit tests
 *
 * @param player - the shaka player
 * @returns number if a bitrate is found.
 */
export function getBitrateInfo(player: ShakaPlayer): number | undefined {
  const stats = player.getStats?.();
  if (stats) {
    return stats.streamBandwidth;
  }
}

/**
 * Generate a handler for audio track changes, putting it here allows us to
 * test the functionality separately and makes it clear what it depends on
 *
 * NOTE: Exported for unit tests
 *
 * @param player - the shaka player
 * @param state - object with the last audio track, so we don't spam the
 * reporter
 * @returns A handler which will dispatch a audio track change when the track
 * changes.
 */
export function generateAudioTrackChangeHandler(
  player: ShakaPlayerExtended,
  state: TechApiState
): Handler<unknown> {
  return (evt: unknown) => {
    const at = getAudioTrackInfo(player);
    if (!tracksMatch(at, state.at)) {
      log.trace('Emitting shaka audio change event', { at, evt });
      state.at = at;
      dispatchPlayerEvent({ e: audioc, at });
    }
  };
}

/**
 * Generate a handler for caption track changes, putting it here allows us to
 * test the functionality separately and makes it clear what it depends on
 *
 * NOTE: Exported for unit tests
 *
 * @param player - the shaka player
 * @param state - object with the last caption track, so we don't spam the
 * reporter
 * @returns A handler which will dispatch a caption track change when the track
 * changes.
 */
export function generateCaptionChangeHandler(
  player: ShakaPlayerExtended,
  state: TechApiState
): Handler<unknown> {
  return (evt: unknown) => {
    const ct = getTextTrackInfo(player);
    if (!tracksMatch(ct, state.ct)) {
      log.trace('Emitting shaka caption change event', { ct, evt });
      dispatchPlayerEvent({ e: capc, ct });
      state.ct = ct;
    }
  };
}

/**
 * Generate a handler for bitrate changes, putting it here allows us to
 * test the functionality separately and makes it clear what it depends on
 *
 * NOTE: Exported for unit tests
 *
 * @param player - the shaka player
 * @param state - object with the last bitrate, so we don't spam the
 * reporter
 * @returns A handler which will dispatch a bitrate change when the bitrate
 * changes.
 */
export function generateBitrateChangeHandler(
  player: ShakaPlayer,
  state: TechApiState
): Handler<unknown> {
  return evt => {
    const b = getBitrateInfo(player);
    if (typeof b !== 'undefined' && b !== state.lastBitrate) {
      log.trace('Emitting shaka bitrate change event', { b, evt });
      dispatchPlayerEvent({ e: pbc, b });
      state.lastBitrate = b;
    }
  };
}
//#endregion Utility functions

/**
 * Add Listeners to the shaka player's events so that we can track the changes
 * to the tracks and bitrate.
 *
 * @param shakaPlayer - the shaka player
 * @param tech - the tech api to push the functions to get active track
 * information and the bitrate.
 * @returns A cleanup method to stop listening to the various events.
 */
const attachShakaEvents = (
  shakaPlayer: ShakaPlayer,
  tech: TechApi
): CleanupMethod => {
  if (typeof shakaPlayer?.addEventListener !== 'function') {
    log.info('Unable to bind shaka events');
    return noop;
  }
  const player = shakaPlayer as ShakaPlayerExtended;
  const state: TechApiState = {};
  const bitratechangeListener = generateBitrateChangeHandler(player, state);
  const audiochangedListener = generateAudioTrackChangeHandler(player, state);
  const captionchangedListener = generateCaptionChangeHandler(player, state);

  setupTechApiFunction(tech, AUDIOCHANGED, () => getAudioTrackInfo(player));
  setupTechApiFunction(tech, BITRATECHANGED, () => getBitrateInfo(player));
  setupTechApiFunction(tech, CAPTIONCHANGED, () => getTextTrackInfo(player));

  // We are subscribing to multiple events because even though the documentation
  // suggests that the correct event is X in practice we are seeing it in Y,
  // also depending on how the change is triggered we will see different events
  player.addEventListener(SHAKA_ADAPTATION_EVENT, bitratechangeListener);
  player.addEventListener(SHAKA_VARIANT_CHANGED, bitratechangeListener);
  player.addEventListener(SHAKA_RATE_CHANGED, bitratechangeListener);
  player.addEventListener(SHAKA_ADAPTATION_EVENT, audiochangedListener);
  player.addEventListener(SHAKA_VARIANT_CHANGED, audiochangedListener);
  player.addEventListener(SHAKA_AUDIO_CHANGED, audiochangedListener);
  player.addEventListener(SHAKA_TEXT_CHANGED, captionchangedListener);
  player.addEventListener(SHAKA_TEXT_TRACK_VIS, captionchangedListener);

  return () => {
    tearDownTechApiFunction(tech, AUDIOCHANGED);
    tearDownTechApiFunction(tech, BITRATECHANGED);
    tearDownTechApiFunction(tech, CAPTIONCHANGED);
    if (typeof player.removeEventListener === 'function') {
      player.removeEventListener(SHAKA_ADAPTATION_EVENT, bitratechangeListener);
      player.removeEventListener(SHAKA_VARIANT_CHANGED, bitratechangeListener);
      player.removeEventListener(SHAKA_RATE_CHANGED, bitratechangeListener);
      player.removeEventListener(SHAKA_ADAPTATION_EVENT, audiochangedListener);
      player.removeEventListener(SHAKA_VARIANT_CHANGED, audiochangedListener);
      player.removeEventListener(SHAKA_AUDIO_CHANGED, audiochangedListener);
      player.removeEventListener(SHAKA_TEXT_CHANGED, captionchangedListener);
      player.removeEventListener(SHAKA_TEXT_TRACK_VIS, captionchangedListener);
    }
  };
};

export default attachShakaEvents;
